import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

export type ProductTabItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

interface ProductTabNavProps {
  tabs: ProductTabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

const ProductTabNav: React.FC<ProductTabNavProps> = ({ tabs, activeTab, onTabChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: { xs: "nowrap", lg: "wrap" },
        alignItems: "center",
        gap: 0.5,
        p: 0.75,
        mb: 2,
        borderRadius: "999px",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)",
        boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.2)" : "0 1px 3px rgba(15,23,42,0.06)",
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {tabs.map((tab) => {
        const isActionTab = typeof tab.onClick === "function";
        const isActive = !isActionTab && activeTab === tab.id;
        const isDisabled = !!tab.disabled && !isActive;

        const activate = () => {
          if (isDisabled) return;
          if (tab.onClick) {
            tab.onClick();
            return;
          }
          onTabChange(tab.id);
        };

        return (
          <Box
            key={tab.id}
            onClick={activate}
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
            title={isDisabled ? "Save the product first to access this section" : undefined}
            onKeyDown={(e) => {
              if (isDisabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate();
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.75,
              py: 0.85,
              borderRadius: "999px",
              cursor: isDisabled ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              userSelect: "none",
              opacity: isDisabled ? 0.45 : 1,
              border: "1px solid transparent",
              background: isActive ? "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" : "transparent",
              boxShadow: isActive ? "0 2px 8px rgba(13,148,136,0.35)" : "none",
              color: isActive ? "#fff" : isDark ? "#94a3b8" : "#64748b",
              transition: "all 0.2s ease",
              "&:hover": isDisabled
                ? undefined
                : {
                    transform: isActive ? "none" : "translateY(-1px)",
                    color: isActive ? "#fff" : isDark ? "#e2e8f0" : "#334155",
                    background: isActive
                      ? "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)"
                      : isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.05)",
                  },
            }}
          >
            <Box sx={{ display: "flex", fontSize: "1rem", color: "inherit" }}>{tab.icon}</Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: isActive ? 600 : 500, lineHeight: 1 }}>
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProductTabNav;
